<template>
  <ui-modal-layout
    :title="tr('Site links')"
    :description="description"
    :icon="LinkIcon"
  >
    <info-text v-if="localSiteLinks.length < 4">
      {{ tr('Add at least 4 site links to display them on your ad.') }}
    </info-text>

    <draggable
      v-model="localSiteLinks"
      :disabled="disabled"
      handle="[data-slot=drag-handle]"
      :animation="200"
      :force-fallback="true"
      class="flex flex-col gap-2.5"
      ghost-class="opacity-40"
      fallback-class="shadow-xl"
    >
      <div
        v-for="(siteLink, index) in localSiteLinks"
        :key="siteLink._uid"
        class="glass-tile flex items-center gap-2 rounded-xl p-3"
      >
        <button
          v-if="!disabled"
          type="button"
          data-slot="drag-handle"
          class="flex shrink-0 cursor-grab touch-none items-center text-muted-foreground active:cursor-grabbing"
          :aria-label="tr('Reorder')"
        >
          <grip-vertical-icon class="size-4" />
        </button>
        <site-link-item
          :site-link="siteLink"
          :disabled="disabled"
          class="min-w-0 flex-1"
          @remove="() => removeSiteLink(index)"
        />
      </div>
    </draggable>

    <template #footer>
      <ui-button variant="outline" :disabled="disabled || localSiteLinks.length >= 15" @click="() => addSiteLink()">
        <plus-icon />
        {{ tr('Add new') }}
      </ui-button>
      <ui-button :disabled="disabled" @click="() => save()">
        {{ tr('Save') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { GripVertical, Link, Plus } from '@lucide/vue';
import { markRaw } from 'vue';
import { VueDraggable as draggable } from 'vue-draggable-plus';

import SiteLink from '@/descriptors/SiteLink.js';
import InfoText from '@/ui/components/InfoText.vue';
import SiteLinkItem from '@/ui/components/SiteLinkItem.vue';

export default {
  name: 'SiteLinksModal',
  components: {
    draggable,
    GripVerticalIcon: GripVertical,
    InfoText,
    PlusIcon: Plus,
    SiteLinkItem,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      LinkIcon: markRaw(Link),
      localSiteLinks: this.modelValue.map(item => new SiteLink(JSON.parse(JSON.stringify(item)))),
    };
  },
  computed: {
    description() {
      return this.tr('With this feature, you can showcase multiple products, services, or subpages within a single ad. Each link (or "card") gets its own unique image, title, and link. This way, users can swipe through the ad and click directly to the subpage that interests them most.');
    },
  },
  methods: {
    addSiteLink() {
      if (this.localSiteLinks.length >= 15) {
        return;
      }

      this.localSiteLinks.push(new SiteLink());
    },
    removeSiteLink(index) {
      this.localSiteLinks.splice(index, 1);
    },
    validate() {
      this.$toast.clear();

      for (const siteLink of this.localSiteLinks) {
        if (!siteLink.site_link_title) {
          this.$toast.error(this.tr('Please enter a display label for all site links.'));
          return false;
        }
        if (!siteLink.site_link_url) {
          this.$toast.error(this.tr('Please enter a URL for all site links.'));
          return false;
        }
        if (!siteLink.image_file_id) {
          this.$toast.error(this.tr('Please upload a thumbnail image for all site links.'));
          return false;
        }
        if (siteLink._uploading) {
          this.$toast.info(this.tr('Please wait for all images to finish uploading.'));
          return false;
        }
      }

      return true;
    },
    save() {
      if (!this.validate()) {
        return;
      }

      this.$emit('update:modelValue', this.localSiteLinks.map(sl => sl.toObject()));
      this.$emit('close');
    },
  },
};
</script>
